/**
 * PreviewMessageService — Manages postMessage communication with the Remotion preview iframe.
 *
 * Sends prop updates to the preview and listens for responses.
 */
export class PreviewMessageService {
  /**
   * @param {HTMLIFrameElement} iframe - The preview iframe element
   * @param {string} targetOrigin - The origin of the Remotion preview service (e.g., "http://localhost:3005")
   */
  constructor(iframe, targetOrigin) {
    this.iframe = iframe;
    this.targetOrigin = targetOrigin || (iframe?.src ? new URL(iframe.src).origin : null);
    this._responseHandler = null;
  }

  /**
   * Update a single prop by dot-notation key.
   * @param {string} key - The Remotion prop key (e.g., "captionStyle.fontSize")
   * @param {*} value - The new value
   */
  updateProp(key, value) {
    this._postMessage({ type: "UPDATE_PROPS", payload: { key, value } });
  }

  /**
   * Replace all props at once.
   * @param {Object} props - Full props object
   */
  setProps(props) {
    this._postMessage({ type: "SET_PROPS", payload: props });
  }

  /**
   * Request current props from the iframe.
   * @returns {Promise<Object>} Current props
   */
  getProps() {
    return new Promise((resolve) => {
      const handler = (event) => {
        if (event.origin !== this.targetOrigin) return;
        if (event.data?.type === "PROPS_RESPONSE") {
          window.removeEventListener("message", handler);
          resolve(event.data.payload);
        }
      };
      window.addEventListener("message", handler);
      this._postMessage({ type: "GET_PROPS" });

      // Timeout after 2 seconds
      setTimeout(() => {
        window.removeEventListener("message", handler);
        resolve(null);
      }, 2000);
    });
  }

  /**
   * Seek the preview player to a specific frame.
   * @param {number} frame - The frame number to seek to
   */
  seekToFrame(frame) {
    this._postMessage({ type: "SEEK_TO_FRAME", payload: { frame } });
  }

  /**
   * Reset props to defaults in the preview.
   */
  resetProps() {
    this._postMessage({ type: "RESET_PROPS" });
  }

  _postMessage(message) {
    if (this.iframe?.contentWindow && this.targetOrigin) {
      this.iframe.contentWindow.postMessage(message, this.targetOrigin);
    }
  }
}
